<template>
  <Modal
    :show="show"
    width="366"
    cancel-text=""
    action-text=""
    @close="handleClose"
  >
    <template #title>
      <span class="text-h2">Update Job Family</span>
    </template>
    <template #content>
      <p class="text-body-1 text-textDarkest mb-4">
        Set a new job family for the selected job profiles.
      </p>
      <JobFamilySelect
        v-model="selectedFamilyUlid"
        outlined
        placeholder="Select job family"
        :job-families="jobFamilies"
        :job-family-groups="jobFamilyGroups"
      />
      <p class="text-body-1 text-textDarkest mb-0 mt-4">
        {{ applyLine }}
      </p>
    </template>
    <template #actions>
      <DpplNewButton
        class="bulk-modal-action text-button-1"
        variant="flat"
        color="primary"
        :disabled="!selectedFamilyUlid"
        :loading="loading"
        @click="handleConfirm"
      >
        Update
      </DpplNewButton>
    </template>
  </Modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import Modal from '@/components/shared/Modal.vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import JobFamilySelect from '@/components/shared/JobFamilySelect.vue';
import {
  buildJobFamilySelectItems,
  findJobFamilyByUlid,
} from '@/helpers/jobFamilyEditorItems';
import { formatFamilyUsage } from '@/components/jobArchitecture/helpers';

const props = defineProps({
  show: { type: Boolean, default: false },
  count: { type: Number, default: 0 },
  draftCount: { type: Number, default: 0 },
  loading: { type: Boolean, default: false },
  jobFamilies: { type: Array, default: () => [] },
  jobFamilyGroups: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'confirm']);

const selectedFamilyUlid = ref(null);

const selectItems = computed(() => buildJobFamilySelectItems({
  jobFamilies: props.jobFamilies,
  jobFamilyGroups: props.jobFamilyGroups,
}));

const applyLine = computed(() => {
  const usage = formatFamilyUsage({
    profileCount: props.count,
    draftCount: props.draftCount,
  });

  if (!usage) {
    return '';
  }

  return `This will apply to ${usage}.`;
});

const resetSelection = () => {
  selectedFamilyUlid.value = null;
};

const handleClose = () => {
  resetSelection();
  emit('close');
};

const handleConfirm = () => {
  if (!selectedFamilyUlid.value) {
    return;
  }

  const match = selectItems.value.find((item) => item.value === selectedFamilyUlid.value);
  const fromOptions = findJobFamilyByUlid(props.jobFamilies, selectedFamilyUlid.value);

  emit('confirm', {
    familyUlid: selectedFamilyUlid.value,
    familyName: match?.title || fromOptions?.name || '',
  });
};

watch(() => props.show, (isOpen) => {
  if (!isOpen) {
    resetSelection();
  }
});
</script>

<style scoped lang="less">
.bulk-modal-action {
  width: 150px;
  height: 40px !important;
  min-height: 40px !important;
}
</style>
